<template>
  <div class="text-center">
    <v-progress-circular :model-value="displayValue" :rotate="rotate" :size="size" :width="width" :color="computedColor">
      <template v-slot:default> {{ formattedValue }} % </template>
    </v-progress-circular>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, computed, withDefaults, defineProps, watch } from 'vue'

  const props = withDefaults(defineProps<{
    value?: number
    size?: number
    width?: number
    color?: string
    rotate?: number
    autoColor?: boolean
    animateOnMount?: boolean
    duration?: number
  }>(), {
    value: 75,
    size: 100,
    width: 15,
    color: 'teal',
    rotate: 360,
    autoColor: true,
    animateOnMount: true,
    duration: 800,
  })

  const displayValue = ref<number>(0)

  let interval: number | undefined
  let rafId: number | undefined

  // Não arredonda para inteiro: preserva consumos pequenos (ex.: 0.01%),
  // que de outra forma apareceriam como 0%.
  const clampPercent = (v: number) => Math.max(0, Math.min(100, v))

  const cancelAnim = () => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = undefined
  }

  const animateTo = (target: number, duration = Math.max(0, props.duration)) => {
    cancelAnim()
    const start = performance.now()
    const from = displayValue.value
    const delta = clampPercent(target) - from

    if (duration === 0 || delta === 0) {
      displayValue.value = clampPercent(target)
      return
    }

    const tick = (now: number) => {
      const elapsed = now - start
      const t = Math.min(1, elapsed / duration)
      // linear easing
      displayValue.value = clampPercent(from + delta * t)
      if (t < 1) {
        rafId = requestAnimationFrame(tick)
      }
    }
    rafId = requestAnimationFrame(tick)
  }

  onMounted(() => {
    // If value is explicitly provided, render static value and do not animate
    if (props.value !== undefined && props.value !== null) {
      const target = clampPercent(props.value)

      if (!props.animateOnMount) {
        displayValue.value = target
        return
      }
      displayValue.value = 0
      animateTo(target)
      return
    }

    // Legacy animated behavior (kept for compatibility)
    displayValue.value = 0
    interval = window.setInterval(() => {
      if (displayValue.value >= 100) {
        displayValue.value = 0
      } else {
        displayValue.value += 10
      }
    }, 1000)
  })

  onBeforeUnmount(() => {
    if (interval) clearInterval(interval)
    if (rafId) cancelAnimationFrame(rafId)
  })

  const size = computed(() => props.size)
  const width = computed(() => props.width)
  const rotate = computed(() => props.rotate)

  // Rótulo exibido no centro: inteiro para valores >= 1%, mas até 2 casas
  // decimais para consumos fracionários pequenos (ex.: 0.01%).
  const formattedValue = computed(() => {
    const v = displayValue.value
    if (v > 0 && v < 1) return parseFloat(v.toFixed(2)).toString()
    return Math.round(v).toString()
  })

  // Compute color based on percentage when autoColor is enabled
  const computedColor = computed(() => {
    if (!props.autoColor) return props.color
    const v = displayValue.value
    if (v < 60) return 'success' // green
    if (v < 85) return 'warning' // amber
    return 'error' // red
  })

  // React to prop changes and animate from current value to new target
  watch(
    () => props.value,
    (nv) => {
      if (nv === undefined || nv === null) return
      const target = clampPercent(nv)
      animateTo(target)
    }
  )
</script>

<style scoped>
.v-progress-circular {
  margin: 1rem;
}
</style>